import { useMemo } from 'react'
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Iconos PNG embebidos vía CDN para evitar el problema de webpack con
// imágenes relativas dentro de leaflet/dist/images.
const iconBase = 'https://unpkg.com/leaflet@1.9.4/dist/images/'
const iconAzul = new L.Icon({
  iconUrl: `${iconBase}marker-icon.png`,
  iconRetinaUrl: `${iconBase}marker-icon-2x.png`,
  shadowUrl: `${iconBase}marker-shadow.png`,
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
})
const iconRojo = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: `${iconBase}marker-shadow.png`,
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41]
})

const FitBounds = ({ puntos }) => {
  const map = useMap()
  if (puntos.length === 0) return null
  if (puntos.length === 1) {
    map.setView(puntos[0], 15, { animate: false })
  } else {
    const bounds = L.latLngBounds(puntos)
    map.fitBounds(bounds, { padding: [40, 40] })
  }
  return null
}

/**
 * Mapa con marcadores de los fichajes y círculos del radio de cada centro.
 * `fichajes` debe traer lat_entrada/lng_entrada/lat_salida/lng_salida y los
 * datos del centro (centro_lat/centro_lng/centro_radio/centro_nombre).
 */
const MapaFichajesLeaflet = ({ fichajes = [], altura = '500px' }) => {
  const { puntos, centros } = useMemo(() => {
    const pts = []
    const centrosMap = new Map()
    fichajes.forEach((f) => {
      if (f.lat_entrada != null && f.lng_entrada != null) {
        pts.push({
          id: `e-${f.id}`,
          lat: Number(f.lat_entrada), lng: Number(f.lng_entrada),
          fuera: !!f.fuera_de_centro_entrada, tipo: 'Entrada',
          empleado: f.empleado_nombre, fecha: f.fecha, hora: f.hora_entrada,
          centro: f.centro_nombre, precision: f.precision_m_entrada
        })
      }
      if (f.lat_salida != null && f.lng_salida != null) {
        pts.push({
          id: `s-${f.id}`,
          lat: Number(f.lat_salida), lng: Number(f.lng_salida),
          fuera: !!f.fuera_de_centro_salida, tipo: 'Salida',
          empleado: f.empleado_nombre, fecha: f.fecha, hora: f.hora_salida,
          centro: f.centro_nombre, precision: f.precision_m_salida
        })
      }
      if (f.centro_lat != null && f.centro_lng != null && f.centro_trabajo_id) {
        if (!centrosMap.has(f.centro_trabajo_id)) {
          centrosMap.set(f.centro_trabajo_id, {
            id: f.centro_trabajo_id,
            nombre: f.centro_nombre,
            lat: Number(f.centro_lat), lng: Number(f.centro_lng),
            radio: Number(f.centro_radio || 100)
          })
        }
      }
    })
    return { puntos: pts, centros: Array.from(centrosMap.values()) }
  }, [fichajes])

  const coordsParaBounds = useMemo(() => {
    const all = puntos.map(p => [p.lat, p.lng])
    centros.forEach(c => all.push([c.lat, c.lng]))
    return all
  }, [puntos, centros])

  // Centro inicial: si no hay puntos, España.
  const centroInicial = coordsParaBounds[0] || [40.42, -3.70]

  return (
    <MapContainer
      center={centroInicial}
      zoom={6}
      scrollWheelZoom
      style={{ height: altura, width: '100%', borderRadius: '6px' }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {centros.map(c => (
        <Circle
          key={`c-${c.id}`}
          center={[c.lat, c.lng]}
          radius={c.radio}
          pathOptions={{ color: '#2563EB', fillColor: '#2563EB', fillOpacity: 0.05, weight: 1 }}
        />
      ))}
      {puntos.map(p => (
        <Marker key={p.id} position={[p.lat, p.lng]} icon={p.fuera ? iconRojo : iconAzul}>
          <Popup>
            <div style={{ fontSize: '12px', minWidth: '160px' }}>
              <div style={{ fontWeight: 600 }}>{p.empleado}</div>
              <div>{p.fecha} · {p.tipo} {p.hora ? p.hora.substring(0, 5) : ''}</div>
              {p.centro && <div style={{ color: '#666' }}>Centro: {p.centro}</div>}
              {p.precision != null && <div style={{ color: '#666' }}>Precisión: ±{p.precision} m</div>}
              <div style={{ marginTop: 4, color: p.fuera ? '#C13030' : '#0F8C66', fontWeight: 500 }}>
                {p.fuera ? '⚠ Fuera del centro' : '✓ Dentro del centro'}
              </div>
              <a
                href={`https://maps.google.com/?q=${p.lat},${p.lng}`}
                target="_blank" rel="noreferrer"
                style={{ display: 'inline-block', marginTop: 4, color: '#2563EB' }}
              >
                Ver en Google Maps ↗
              </a>
            </div>
          </Popup>
        </Marker>
      ))}
      <FitBounds puntos={coordsParaBounds} />
    </MapContainer>
  )
}

export default MapaFichajesLeaflet
